import type { Schemas } from "@softmax/observatory-client/types";
import { type GradedEpisode, type PitchSubmission } from "../../desk-records";

import { maxConcurrentReads, mapLimit } from "./parallel-reads";
import { entryPageSize, type PitchCursor } from "../pagination";

type RoundEpisodePage = Schemas["RoundEpisodePage"];

type V2EpisodeDetail = Schemas["V2EpisodeDetail"];

function isEpisodeRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

type EpisodeRow = {
  episodeId: string;
  completedAt: string;
  policyVersionIds: string[];
};

export type GradingRound = {
  roundId: string;
  completedAt: string;
  cursor: string | null;
};

/** The newest completed grading episode per policy version, from rounds already ordered newest first. */
function newestEpisodePerPolicyVersion(rows: EpisodeRow[]): EpisodeRow[] {
  const chosen = new Map<string, EpisodeRow>();

  for (const row of rows.toSorted((a, b) =>
    b.completedAt.localeCompare(a.completedAt),
  )) {
    for (const id of row.policyVersionIds) {
      if (!chosen.has(id)) chosen.set(id, row);
    }
  }

  return [...new Set(chosen.values())];
}

export async function readGradedEpisodes({
  get,
  rounds,
}: {
  readonly get: <T>(path: string) => Promise<T>;
  readonly rounds: readonly GradingRound[];
}): Promise<{
  episodes: GradedEpisode[];
  pendingEpisodes: PitchCursor["episodes"];
}> {
  const episodePages = await mapLimit({
    items: rounds,
    limit: maxConcurrentReads,
    fn: async (round) => {
      const params = new URLSearchParams({
        limit: String(entryPageSize),
        completed_only: "true",
      });

      if (round.cursor) params.set("cursor", round.cursor);

      const page = await get<RoundEpisodePage>(
        `/v2/rounds/${encodeURIComponent(round.roundId)}/episodes?${params}`,
      );
      return { round, page };
    },
  });
  const pendingEpisodes: PitchCursor["episodes"] = [];
  const episodeRows: EpisodeRow[] = [];

  for (const { round, page } of episodePages) {
    if (page.next_cursor)
      pendingEpisodes.push({ ...round, cursor: page.next_cursor });

    for (const row of page.entries) {
      if (!row.episode_id || row.status !== "completed") continue;

      episodeRows.push({
        episodeId: String(row.episode_id),
        completedAt: round.completedAt,
        policyVersionIds: row.policy_version_ids.map(String),
      });
    }
  }

  const episodes = await mapLimit({
    items: newestEpisodePerPolicyVersion(episodeRows),
    limit: maxConcurrentReads,
    fn: async (row): Promise<GradedEpisode> => {
      const detail = await get<V2EpisodeDetail>(
        `/v2/episodes/${encodeURIComponent(row.episodeId)}`,
      );
      const coworld = detail.attributes?.coworld;
      const results =
        isEpisodeRecord(coworld) && isEpisodeRecord(coworld.results)
          ? coworld.results
          : null;
      return { ...row, results };
    },
  });
  return { episodes, pendingEpisodes };
}

/** Rows shaped like submissions for the graded entries an anonymous read can see. */
export function gradedAsSubmissions(
  episodes: GradedEpisode[],
): PitchSubmission[] {
  const rows: PitchSubmission[] = [];
  const seen = new Set<string>();

  for (const episode of episodes) {
    const playerNames = episode.results?.player_names;
    const names: unknown[] = Array.isArray(playerNames) ? playerNames : [];
    episode.policyVersionIds.forEach((id, seat) => {
      if (seen.has(id)) return;

      seen.add(id);
      const name = names[seat];
      rows.push({
        id: `graded:${id}`,
        status: "placed",
        created_at: episode.completedAt,
        policy_version: {
          id,
          player_id: null,
          label: "entry:v0",
        },
        player: typeof name === "string" ? { id: "", name } : null,
        notes: null,
      });
    });
  }

  return rows;
}
